/**
 * Artifact panel sync helpers (#131).
 * Pure — used by AutoV2Panel and selftests.
 */
import { resolveDraftArtifactSlug } from './draftArtifactSlug.js';

/**
 * @param {string} text
 * @returns {{ artifact: { type: string, label: string, content: string } | null, cleanText: string }}
 */
export function parseArtifact(text) {
  if (!text) return { artifact: null, cleanText: text };

  const tagPattern = /\[ARTIFACT([^\]]*)\]([\s\S]*?)\[\/ARTIFACT\]/i;
  const match = text.match(tagPattern);

  if (!match) return { artifact: null, cleanText: text };

  const attrString = match[1] || '';
  const content = match[2]?.trim() || '';

  const typeMatch = attrString.match(/type="([^"]+)"/i);
  const labelMatch = attrString.match(/label="([^"]+)"/i);
  // slug is written by appendDraftArtifactFromSaveResults from 2026-09-13 on.
  // Older messages do not carry it; see draftArtifactSlug.js for the fallback.
  const slugMatch = attrString.match(/slug="([^"]+)"/i);

  const artifact = {
    type: typeMatch?.[1] || 'draft',
    label: labelMatch?.[1] || 'Artifact',
    content,
    ...(slugMatch ? { slug: slugMatch[1].trim() } : {}),
  };

  const cleanText = text.replace(tagPattern, '').trim();
  return { artifact, cleanText };
}

/**
 * Prefer the latest assistant message's artifact; if missing, walk backward.
 * Does not clear — returns null only when no message in the thread has a tag.
 * @param {Array<{ role?: string, content?: string }>} msgs
 */
export function findLatestArtifactInMessages(msgs = []) {
  const all = Array.isArray(msgs) ? msgs : [];
  for (const m of [...all].reverse()) {
    if (String(m?.role || '') !== 'assistant') continue;
    const { artifact } = parseArtifact(String(m.content || ''));
    if (artifact) return artifact;
  }
  return null;
}

/**
 * Decide panel artifact for a message list.
 * @returns {{
 *   artifact: object|null,
 *   clear: boolean,
 *   source: 'latest'|'fallback'|'none'|'empty_thread'
 * }}
 */
export function resolveArtifactPanelState(msgs = []) {
  const all = Array.isArray(msgs) ? msgs : [];
  const hasAssistant = all.some((m) => String(m?.role || '') === 'assistant');
  if (!hasAssistant) {
    return { artifact: null, clear: true, source: 'empty_thread' };
  }

  const lastAssistant = [...all].reverse().find((m) => String(m?.role || '') === 'assistant');
  const fromLatest = parseArtifact(String(lastAssistant?.content || '')).artifact;
  if (fromLatest) {
    return { artifact: keepPostTabs(fromLatest, all), clear: false, source: 'latest' };
  }

  const fallback = findLatestArtifactInMessages(all);
  if (fallback) {
    const moved = findNewerDraftWork(all, fallback);
    if (moved) return { artifact: moved, clear: false, source: 'tools' };
    return { artifact: keepPostTabs(fallback, all), clear: false, source: 'fallback' };
  }

  // A teammate worked on a draft that was written in another conversation
  // (approved it, made its image, scheduled it). Bart, 2026-10-25: "All my tabs
  // on the right above the artifact panel are now gone." Open that draft here
  // so its tabs follow the work.
  const worked = findDraftWorkedByTools(all);
  if (worked) {
    return { artifact: worked, clear: false, source: 'tools' };
  }

  // Conversational turn with no tags in history — keep existing panel (caller must not clear).
  return { artifact: null, clear: false, source: 'none' };
}

/**
 * A journal post's captions belong in that post's Captions tab, never in a
 * panel of their own. 2026-10-27: Cam sent the caption set as its own panel
 * item, the post's tabs disappeared, and Bart saw the approved header stacked
 * over raw caption text: "The used to be dropped into the Captions tab on the
 * artifact panel. What happened?" When the thread has a journal post, the post
 * stays in the panel and its Captions tab reads the captions from the chat.
 */
export function keepPostTabs(artifact, msgs = []) {
  if (!artifact || artifact.type !== 'captions') return artifact;
  const all = Array.isArray(msgs) ? msgs : [];
  let post = null;
  for (const m of [...all].reverse()) {
    if (String(m?.role || '') !== 'assistant') continue;
    const { artifact: a } = parseArtifact(String(m.content || ''));
    if (a && a.type === 'draft') {
      post = a;
      break;
    }
  }
  post = post || findDraftWorkedByTools(all);
  if (!post || !captionsAreForOtherPosts(artifact, post)) return post || artifact;
  return artifact;
}

/**
 * True when a caption set links to other posts and never names this one.
 * 2026-10-28: in the Automated posts chat the Nov 5, 10 and 12 quote card
 * captions were swapped for The Josiah Archetype, a post worked on earlier in
 * the same chat, so Bart was asked to approve 21 captions he could not see.
 */
export function captionsAreForOtherPosts(captions, post) {
  const text = `${captions?.label || ''}\n${captions?.title || ''}\n${captions?.content || ''}`.toLowerCase();
  const linked = [...text.matchAll(/(?:\/journal\/|[?&]slug=)([a-z0-9-]+)/g)].map((x) => x[1]);
  if (linked.length === 0) return false;
  const slug = String(post?.slug || '').toLowerCase();
  const title = String(post?.label || post?.title || '').toLowerCase().trim();
  if (slug && (linked.includes(slug) || text.includes(slug))) return false;
  if (title && text.includes(title)) return false;
  return true;
}

/**
 * The chat moved on to another post since the last one it handed over. 2026-11-04:
 * the Next Biblical Archetype chat was on the Eli outline while the panel kept
 * showing the finished Josiah post, the last one sent as a panel item. Asking
 * about a post's schedule is not working on it, so that does not count.
 */
function findNewerDraftWork(msgs, shown) {
  let start = 0;
  for (let i = msgs.length - 1; i >= 0; i -= 1) {
    if (String(msgs[i]?.role || '') === 'assistant' && parseArtifact(String(msgs[i].content || '')).artifact) {
      start = i + 1;
      break;
    }
  }
  const worked = findDraftWorkedByTools(msgs.slice(start), new Set(['get_schedule_status']));
  if (!worked || worked.slug === resolveDraftArtifactSlug(shown)) return null;
  return worked;
}

const DRAFT_TOOLS = new Set([
  'save_draft',
  'present_outline',
  'approve_draft',
  'approve_stage',
  'generate_image',
  'schedule_captions',
  'schedule_journal_publish',
  'get_schedule_status',
  'publish_journal',
]);

/** The newest journal draft a reply's tools touched, as a panel artifact to load by slug. */
export function findDraftWorkedByTools(msgs = [], skipTools = null) {
  const all = Array.isArray(msgs) ? msgs : [];
  for (let i = all.length - 1; i >= 0; i -= 1) {
    const m = all[i];
    if (String(m?.role || '') !== 'assistant') continue;
    const rows = Array.isArray(m?.meta?.tool_results) ? m.meta.tool_results : [];
    for (let j = rows.length - 1; j >= 0; j -= 1) {
      const row = rows[j];
      if (!row || !DRAFT_TOOLS.has(row.name) || skipTools?.has(row.name)) continue;
      const r = row.result && typeof row.result === 'object' ? row.result : {};
      const slug = String(r.slug || r.draft?.slug || '').trim();
      if (!slug || r.ok === false) continue;
      if (r.kind && r.kind !== 'journal') continue;
      return { type: 'draft', slug, label: String(r.title || r.draft?.title || slug), content: '' };
    }
  }
  return null;
}
